/**
 * @fileoverview "Assign Target" dialog for Sales Pipeline (POST /sales/targets).
 * @description One step: the target's name, assignee, period (type, then one of that type's open
 * periods from GET /sales/targets/periods) and priority, above the selected backlog prospects;
 * "Confirm & Assign" sends the whole selection as one all-or-nothing batch. Assigning also makes the
 * assignee each prospect's owner. The assignee choices come from GET /sales/targets/eligible-assignees
 * (a Manager sees their own team); when every selected prospect already has the same owner and that
 * owner may take a target, they are chosen to start with. The backend rechecks everything, so a
 * refusal (a prospect already has a target, the assignee is out of scope, a recent sign-in is
 * needed) rolls the batch back and shows as the dialog's error. The prospects are the selection as
 * it was when the dialog opened (the parent passes a snapshot), so a background refetch cannot
 * change what is posted. Close is held while the request is in flight, and the confirmation runs
 * from the mutation itself (so it survives an unmount). The parent mounts it only while it's open,
 * so every opening starts clean.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useRef } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useAssignProspectTargets } from '@/features/sales/hooks/useAssignProspectTargets'
import { useEligibleTargetAssignees } from '@/features/sales/hooks/useEligibleTargetAssignees'
import { useTargetPeriods } from '@/features/sales/hooks/useTargetPeriods'
import {
  assignProspectTargetFormSchema,
  type AssignProspectTargetFormValues,
} from '@/features/sales/schemas/assign-prospect-target-form.schema'
import Modal from '@/shared/components/molecules/Modal'
import SelectField from '@/shared/components/molecules/SelectField'
import { useGuardedClose } from '@/shared/hooks/useGuardedClose'

import { PriorityPicker, SelectedProspectsPanel } from './AssignTargetPeriodParts'
import DialogActions from './DialogActions'
import SalesRequestError from './SalesRequestError'

import type { ProspectListItem } from '@/features/sales/types/prospect.types'
import type {
  AssignTargetBatchResult,
  EligibleTargetAssignee,
  SalesTargetPeriodType,
} from '@/features/sales/types/sales-target.types'
import type { FormEvent } from 'react'

/** @description The period types, with the translation key of each one's label. */
const PERIOD_TYPES: { value: SalesTargetPeriodType; labelKey: string }[] = [
  { value: 'MONTHLY', labelKey: 'Monthly' },
  { value: 'QUARTERLY', labelKey: 'Quarterly' },
  { value: 'BI_ANNUAL', labelKey: 'Half-Yearly' },
  { value: 'ANNUAL', labelKey: 'Annual' },
]

const EMPTY_ASSIGNEES: EligibleTargetAssignee[] = []

/**
 * @description The owner every selected prospect shares, if they all have the same one.
 * @param {ProspectListItem[]} prospects - The selected prospects.
 * @returns {string} That owner's user id, or '' when they differ or any is unowned.
 */
function sharedOwnerId(prospects: ProspectListItem[]): string {
  const owner = prospects[0]?.currentOwner?.userId ?? ''
  return owner && prospects.every((prospect) => prospect.currentOwner?.userId === owner)
    ? owner
    : ''
}

/**
 * @description A selected row's prospect id.
 * @param {ProspectListItem} prospect - The row's prospect.
 * @returns {string} Its id.
 */
const toProspectId = (prospect: ProspectListItem) => prospect.prospectId

interface AssignProspectTargetModalProps {
  prospects: ProspectListItem[]
  onClose: () => void
  onAssigned: (result: AssignTargetBatchResult) => void
}

/**
 * @component
 * @param {AssignProspectTargetModalProps} props - The selected prospects and the callbacks.
 * @param {ProspectListItem[]} props.prospects - The backlog rows being assigned.
 * @param {() => void} props.onClose - Called to dismiss the dialog without assigning.
 * @param {(result: AssignTargetBatchResult) => void} props.onAssigned - Called once the batch is
 * saved (clears the row selection).
 */
function AssignProspectTargetModal({
  prospects,
  onClose,
  onAssigned,
}: AssignProspectTargetModalProps) {
  const { t } = useTranslation()
  const assign = useAssignProspectTargets({ onSuccess: onAssigned })
  const close = useGuardedClose(onClose, assign.isPending)
  const assigneesQuery = useEligibleTargetAssignees(true)
  const assignees = assigneesQuery.data ?? EMPTY_ASSIGNEES
  const {
    register,
    handleSubmit,
    control,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<AssignProspectTargetFormValues>({
    resolver: zodResolver(assignProspectTargetFormSchema),
    defaultValues: {
      name: '',
      assigneeUserId: '',
      periodType: 'QUARTERLY',
      periodLabel: '',
      priority: 'MEDIUM',
    },
  })
  const periodType = useWatch({ control, name: 'periodType' })
  const priority = useWatch({ control, name: 'priority' })
  const periodsQuery = useTargetPeriods(periodType)
  const periods = periodsQuery.data?.periods

  const periodsSettled = periodsQuery.isSuccess && !periodsQuery.isFetching
  const prefilledType = useRef<string | null>(null)

  // The period picked under one type has no equivalent under another, so the first time a type's
  // periods have settled keep the choice if it is still offered and otherwise take the earliest.
  // Running once per type (and only after `!isFetching`) means a background refetch never blanks
  // or overrides a period the user picked.
  useEffect(() => {
    if (!periodsSettled || !periods || prefilledType.current === periodType) return
    prefilledType.current = periodType
    if (!periods.some((period) => period.periodLabel === getValues('periodLabel'))) {
      setValue('periodLabel', periods[0]?.periodLabel ?? '')
    }
  }, [periodsSettled, periods, periodType, getValues, setValue])

  // Start with the owner the prospects already share, once the choices say they may take a target.
  useEffect(() => {
    const owner = sharedOwnerId(prospects)
    if (owner && !getValues('assigneeUserId') && assignees.some((one) => one.userId === owner)) {
      setValue('assigneeUserId', owner)
    }
  }, [assignees, prospects, getValues, setValue])

  /**
   * @description Sends the validated batch; the mutation reports the result.
   * @param {AssignProspectTargetFormValues} values - The form's values.
   */
  const onValid = (values: AssignProspectTargetFormValues) => {
    assign.mutate({ ...values, prospectIds: prospects.map(toProspectId) })
  }

  /**
   * @description Validates and submits the form.
   * @param {FormEvent<HTMLFormElement>} event - The submit event.
   */
  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    void handleSubmit(onValid)(event)
  }

  const placeholder = { value: '', label: t('sales.pipeline.form.selectPlaceholder') }
  const managers = assignees.filter((one) => one.positionCode === 'SALES_MANAGER')
  const executives = assignees.filter((one) => one.positionCode === 'SALES_EXECUTIVE')
  /**
   * @description Labels an assignee as their name and, when known, employee code.
   * @param {EligibleTargetAssignee} one - The assignee.
   * @returns {string} The option label.
   */
  const assigneeLabel = (one: EligibleTargetAssignee) =>
    one.employeeCode ? `${one.displayName} (${one.employeeCode})` : one.displayName
  const assigneeError = errors.assigneeUserId?.message
  const periodError = errors.periodLabel?.message
  const tooMany = prospects.length > 100

  return (
    <Modal
      open
      onClose={close}
      data-testid="assign-target-period-modal"
      title={t('sales.pipeline.assign.title')}
      subtitle={t('sales.pipeline.assign.subtitle', { count: prospects.length })}
      size="lg"
    >
      <form
        id="assign-target-period-form"
        data-testid="assign-target-period-form"
        onSubmit={onSubmit}
        noValidate
        className="grid grid-cols-1 gap-4 sm:grid-cols-2"
      >
        <div className="col-span-full flex flex-col gap-1.5">
          <label htmlFor="targetName" className="text-[12.5px] font-semibold text-cell">
            {t('sales.pipeline.assign.fields.name')}
          </label>
          <input
            id="targetName"
            data-testid="assign-target-period-name-input"
            type="text"
            maxLength={150}
            placeholder={t('sales.pipeline.assign.fields.namePlaceholder')}
            className="w-full rounded-lg border border-input-border bg-white px-3 py-2 text-[13.5px] text-ink"
            {...register('name')}
          />
          {errors.name?.message && (
            <span
              className="text-[11.5px] font-medium text-danger"
              data-testid="assign-target-period-name-error"
            >
              {t(errors.name.message)}
            </span>
          )}
        </div>

        <div className="col-span-full flex flex-col gap-1.5">
          <SelectField
            id="assignee"
            data-testid="assign-target-period-assignee-select"
            label={t('sales.pipeline.assign.fields.assignee')}
            disabled={!assigneesQuery.isSuccess || assignees.length === 0}
            options={[
              placeholder,
              ...managers.map((one) => ({ value: one.userId, label: assigneeLabel(one) })),
              ...executives.map((one) => ({ value: one.userId, label: assigneeLabel(one) })),
            ]}
            error={assigneeError ? t(assigneeError) : undefined}
            {...register('assigneeUserId')}
          />
          {assigneesQuery.isSuccess && assignees.length === 0 && (
            <span
              className="text-[11.5px] text-slate"
              data-testid="assign-target-period-no-assignees-message"
            >
              {t('sales.pipeline.assign.fields.assigneeNone')}
            </span>
          )}
          {assigneesQuery.isError && <SalesRequestError error={assigneesQuery.error} />}
          <span
            className="text-[11.5px] text-footer"
            data-testid="assign-target-period-assignee-hint"
          >
            {t('sales.pipeline.assign.fields.assigneeHint')}
          </span>
        </div>

        <SelectField
          id="periodType"
          data-testid="assign-target-period-period-type-select"
          label={t('sales.pipeline.assign.fields.periodType')}
          options={PERIOD_TYPES.map((type) => ({
            value: type.value,
            label: t(`sales.pipeline.assign.periodTypes.${type.labelKey}`),
          }))}
          {...register('periodType')}
        />

        <SelectField
          id="periodLabel"
          data-testid="assign-target-period-period-select"
          label={t('sales.pipeline.assign.fields.period')}
          disabled={!periods || periods.length === 0}
          options={(periods ?? []).map((period) => ({
            value: period.periodLabel,
            label: `${period.periodLabel} (${period.range})`,
          }))}
          error={periodError ? t(periodError) : undefined}
          {...register('periodLabel')}
        />
        {periodsQuery.isError && (
          <div className="col-span-full">
            <SalesRequestError error={periodsQuery.error} />
          </div>
        )}

        <PriorityPicker value={priority} onChange={(next) => setValue('priority', next)} />

        <SelectedProspectsPanel prospects={prospects} />
        {tooMany && (
          <p
            role="alert"
            className="col-span-full text-[13px] text-danger"
            data-testid="assign-target-period-too-many-error"
          >
            {t('sales.pipeline.assign.errors.tooMany')}
          </p>
        )}
        {assign.isError && (
          <div
            data-testid="assign-target-period-error"
            className="col-span-full rounded-lg border border-danger/40 bg-danger-bg px-3.5 py-2.5"
          >
            <SalesRequestError error={assign.error} />
          </div>
        )}
        <div className="col-span-full">
          <DialogActions
            testIdPrefix="assign-target-period"
            cancelLabel={t('sales.pipeline.form.cancel')}
            confirmLabel={t('sales.pipeline.assign.confirmSubmit')}
            onCancel={close}
            confirmType="submit"
            isLoading={assign.isPending}
            disabled={
              !assigneesQuery.isSuccess || assignees.length === 0 || !periodsSettled || tooMany
            }
          />
        </div>
      </form>
    </Modal>
  )
}

export default AssignProspectTargetModal
